import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Settings } from 'luxon'
import { sortBy } from 'lodash'
import {
  appLocales,
  formatTranslationMessages,
  fillGaps,
  normalizeTranslationMessages
} from '../../i18n'
import { DEFAULT_LOCALE } from '../../containers/app/constants'
import {
  messages as enMessages,
  countryNames as enCountryNames
} from './chunks/en'
import { shallow } from 'zustand/shallow'
// don't remove this import
import '../../redux-store'

export const getCountryNamesAsOptions = () => {
  const countryNames = { ...useLanguageStore.getState().countryNames }
  delete countryNames.default

  return sortBy(
    Object.entries(countryNames).map(([code, name]) => ({
      label: name,
      value: code
    })),
    ['label']
  )
}

const getBrowserLocale = () => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE
  const tag = (navigator.language || navigator.userLanguage || '').toLowerCase()
  if (appLocales.includes(tag)) return tag
  const lang = tag.substring(0, 2)
  return appLocales.includes(lang) ? lang : DEFAULT_LOCALE
}

// Função para carregar os chunks de idioma dinamicamente
const importLanguageChunk = async (locale) => {
  switch (locale) {
    case 'en':
      return import('./chunks/en')
    case 'de':
      return import('./chunks/de')
    case 'pl':
      return import('./chunks/pl')
    case 'cs':
      return import('./chunks/cs')
    case 'bg':
      return import('./chunks/bg')
    case 'da':
      return import('./chunks/da')
    case 'fr':
      return import('./chunks/fr')
    case 'hu':
      return import('./chunks/hu')
    case 'sk':
      return import('./chunks/sk')
    case 'tr':
      return import('./chunks/tr')
    case 'pt-br':
      return import('./chunks/pt-br')
    case 'it':
      return import('./chunks/it')
    case 'es':
      return import('./chunks/es')
    case 'sr':
      return import('./chunks/sr')
    case 'sv':
      return import('./chunks/sv')
    case 'hr':
      return import('./chunks/hr')
    case 'ru':
      return import('./chunks/ru')
    case 'ro':
      return import('./chunks/ro')
    case 'sl':
      return import('./chunks/sl')
    case 'nl':
      return import('./chunks/nl')
    case 'no':
      return import('./chunks/no')
    default:
      return import('./chunks/en')
  }
}

export const useLanguageStore = create(
  persist(
    (set) => ({
      locale: getBrowserLocale(),
      messages: normalizeTranslationMessages(enMessages),
      countryNames: enCountryNames,

      changeLocale: async (locale) => {
        const { countryNames, messages } = await importLanguageChunk(locale)
        Settings.defaultLocale = locale

        const formattedMessages = fillGaps(
          formatTranslationMessages(locale, messages)
        )

        set({
          locale,
          messages: formattedMessages,
          countryNames
        })
      }
    }),
    {
      name: 'language',
      partialize: (state) => ({ locale: state.locale }),
      onRehydrateStorage: () => (state) => {
        if (state && state.locale && state.locale !== DEFAULT_LOCALE) {
          state.changeLocale(state.locale)
        }
      }
    }
  )
)

// First-load case (no persisted value): if browser locale isn't the default,
// load its messages chunk so the UI matches `locale` from the start.
if (
  typeof window !== 'undefined' &&
  useLanguageStore.getState().locale !== DEFAULT_LOCALE &&
  !localStorage.getItem('language')
) {
  useLanguageStore.getState().changeLocale(useLanguageStore.getState().locale)
}

// Selectors
export const useCountryNames = () => {
  const countryNames = useLanguageStore(
    (state) => state.countryNames ?? [],
    shallow
  )

  const defineCountryNames = countryNames.default ?? countryNames

  if (defineCountryNames) {
    const options = Object.entries(defineCountryNames).map(([code, name]) => {
      return {
        label: name,
        value: code
      }
    })
    return sortBy(options, ['label'])
  }

  return []
}

export const getChangeLocaleStatic = () =>
  useLanguageStore.getState().changeLocale ?? null
